import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, Video, ClipboardList, ArrowRight, Clock, Users,
  ArrowLeft, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Webinar invite, pick a session + register (capture only)
// ---------------------------------------------------------------------------
export const HomecareWebinar = () => {
  const [meta, setMeta] = useState({ title: '', duration_mins: 45, slots: [] });
  const [slot, setSlot] = useState('');
  const [form, setForm] = useState({ name: '', organisation: '', role: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/webinar/slots`)
      .then((r) => { setMeta(r.data); if (r.data.slots?.[0]) setSlot(r.data.slots[0].id); })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error('Email required'); return; }
    if (!slot) { toast.error('Please pick a session'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/webinar/register`, {
        email: form.email.trim(), name: form.name.trim() || null,
        organisation: form.organisation.trim() || null, role: form.role.trim() || null, slot,
      });
      setDone(true);
      toast.success("You're registered.");
      try { const sid = localStorage.getItem('ctc_sid'); if (sid) axios.post(`${API}/public/track/conversion`, { sid, type: 'webinar' }).catch(() => {}); } catch (e) { /* ignore */ }
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const agenda = [
    'The 6 questions that separate a good all-in-one from a bad fit',
    'Rostering, care planning, eMAR & invoicing, what "all-in-one" really covers',
    'How to run a fair supplier comparison (and avoid lock-in)',
    'Live Q&A with an independent adviser',
  ];

  return (
    <div data-testid="page-homecare-webinar">
      <section className="bg-gradient-to-br from-[#00A79D] via-[#2a1fb8] to-[#F5871F] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Link to="/how-to-choose" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white mb-6" data-testid="webinar-back">
              <ArrowLeft className="w-4 h-4" /> Back to the guide
            </Link>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 px-3 py-1 rounded-full mb-5">
              <Video className="w-3.5 h-3.5" /> Free live webinar
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold leading-tight">{meta.title || 'How to choose your all-in-one homecare system'}</h1>
            <div className="flex items-center gap-5 mt-5 text-sm text-gray-200">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-white" /> {meta.duration_mins} minutes</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-white" /> Independent, no sales pitch</span>
            </div>
            <div className="mt-8">
              <h2 className="font-heading text-lg font-semibold mb-3">What we'll cover</h2>
              <ul className="space-y-2.5">
                {agenda.map((a) => (
                  <li key={a} className="flex gap-2.5 text-gray-200 text-sm"><CheckCircle2 className="w-4 h-4 text-white flex-shrink-0 mt-0.5" /> {a}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 lg:sticky lg:top-24 text-gray-900">
            {done ? (
              <div className="text-center py-6" data-testid="webinar-form-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
                <p className="font-semibold text-lg">You're registered.</p>
                <p className="text-sm text-gray-600 mt-1">We'll email your joining link before the session.</p>
                <Link to="/homecare-questionnaire" className="inline-flex items-center gap-1.5 text-[#00A79D] font-semibold mt-5" data-testid="webinar-to-questionnaire">
                  Get a tailored shortlist too <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="webinar-form">
                <h2 className="font-heading text-xl font-semibold mb-1">Save your seat</h2>
                <p className="text-sm text-gray-500 mb-2">Pick a session that suits you.</p>
                <div className="space-y-2">
                  {(meta.slots || []).map((s) => (
                    <label key={s.id} className={`flex items-center gap-2.5 border rounded-md px-3 py-2.5 cursor-pointer text-sm transition-colors ${slot === s.id ? 'border-[#00A79D] bg-[#00A79D]/5' : 'border-[#E5E7EB] hover:border-gray-300'}`} data-testid={`webinar-slot-${s.id}`}>
                      <input type="radio" name="slot" value={s.id} checked={slot === s.id} onChange={() => setSlot(s.id)} className="accent-[#00A79D]" />
                      <span className="font-medium text-gray-800">{s.label}</span>
                    </label>
                  ))}
                </div>
                <input type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40" data-testid="webinar-name" />
                <input type="text" placeholder="Organisation" value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40" data-testid="webinar-org" />
                <input type="text" placeholder="Your role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40" data-testid="webinar-role" />
                <input type="email" required placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40" data-testid="webinar-email" />
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="webinar-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />} Register free
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Buyer-needs questionnaire, capture requirements → shortlist promise
// ---------------------------------------------------------------------------
const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-[#00A79D] border-[#00A79D] text-white' : 'bg-white border-[#E5E7EB] text-gray-700 hover:border-[#00A79D]'}`}>
    {children}
  </button>
);

export const HomecareQuestionnaire = () => {
  const [meta, setMeta] = useState({ priorities: [], provider_types: [], location_bands: [], timelines: [] });
  const [f, setF] = useState({
    name: '', organisation: '', email: '', provider_type: '', num_locations: '',
    current_systems: '', priorities: [], timeline: '', budget: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { axios.get(`${API}/public/questionnaire/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const togglePriority = (p) => setF((s) => ({
    ...s, priorities: s.priorities.includes(p) ? s.priorities.filter((x) => x !== p) : [...s.priorities, p],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.trim()) { toast.error('Email required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/questionnaire`, {
        ...f, email: f.email.trim(), name: f.name.trim() || null, organisation: f.organisation.trim() || null,
      });
      setDone(true);
      toast.success('Thank you, shortlist on the way.');
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setBusy(false); }
  };

  const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';

  if (done) return (
    <div data-testid="page-homecare-questionnaire">
      <section className="bg-[#F9FAFB]">
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-10" data-testid="questionnaire-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-extrabold text-gray-900">Thank you.</h1>
            <p className="text-gray-600 mt-3">We'll review what matters for your service and build a tailored shortlist, or a warm introduction to the right suppliers.</p>
            <Link to="/browse" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md mt-6 transition-colors" data-testid="questionnaire-browse">
              Browse the directory <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );

  return (
    <div data-testid="page-homecare-questionnaire">
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/how-to-choose" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00A79D] mb-6" data-testid="questionnaire-back">
            <ArrowLeft className="w-4 h-4" /> Back to the guide
          </Link>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-4">
            <ClipboardList className="w-3.5 h-3.5" /> 2-minute questionnaire
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">Tell us what matters, we'll build your shortlist.</h1>
          <p className="text-gray-600 mt-3">No sales pitch. We carry the complexity and route you to genuinely suitable options.</p>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="questionnaire-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <input type="text" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="q-name" />
              <input type="text" placeholder="Organisation" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid="q-org" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">What kind of service are you?</label>
              <div className="flex flex-wrap gap-2">
                {meta.provider_types.map((p) => (
                  <Chip key={p} active={f.provider_type === p} onClick={() => setF({ ...f, provider_type: p })} testid={`q-provider-${p}`}>{p}</Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">How many locations / branches?</label>
              <div className="flex flex-wrap gap-2">
                {meta.location_bands.map((b) => (
                  <Chip key={b} active={f.num_locations === b} onClick={() => setF({ ...f, num_locations: b })} testid={`q-loc-${b}`}>{b}</Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">What matters most? (pick any)</label>
              <div className="flex flex-wrap gap-2">
                {meta.priorities.map((p) => (
                  <Chip key={p} active={f.priorities.includes(p)} onClick={() => togglePriority(p)} testid={`q-priority-${p}`}>{p}</Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">When are you looking to decide?</label>
              <div className="flex flex-wrap gap-2">
                {meta.timelines.map((t) => (
                  <Chip key={t} active={f.timeline === t} onClick={() => setF({ ...f, timeline: t })} testid={`q-timeline-${t}`}>{t}</Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">What are you using today? (optional)</label>
              <input type="text" placeholder="e.g. paper, spreadsheets, or a named system" value={f.current_systems} onChange={(e) => setF({ ...f, current_systems: e.target.value })} className={field} data-testid="q-current-systems" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Anything else we should know? (optional)</label>
              <textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={field} data-testid="q-notes" />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-2">Work email <span className="text-red-500">*</span></label>
              <input type="email" required placeholder="you@yourservice.co.uk" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="q-email" />
            </div>

            <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="q-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Get my shortlist
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};
